// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Navigate, useParams } from '@tanstack/react-router';

// material ui icons
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded';

// material ui
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

// api
import {
  ActuatorSetPointDto,
  useGetAllActuatorPlansQuery,
  useGetV2GatewaysQuery,
  usePostActuatorPlanMutation,
} from '@/api/generated/recalc/endpoints/v2/gateways';

// shared components
import { ServiceGuard } from '@/components/access-control/ServiceGuard';
// components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// components
import ScheduleDatagrid from '@/features/weekschedule-manager/ScheduleDatagrid';
import WeekScheduleEditor from '@/features/weekschedule-manager/WeekScheduleEditor.tsx';
import { showSnackbar } from '@/redux/snackbarSlice';

const CHANNEL_OPTIONS = ['output0', 'output1', 'output2', 'output3'] as const;
const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface WeekDayEntry {
  dayIndex: WeekdayIndex;
  enabled: boolean;
  timeFrom: string;
  timeTo: string;
}

const defaultEndDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 2);
  return d.toISOString().slice(0, 10);
};

const defaultWeekSchedule = (): WeekDayEntry[] =>
  WEEKDAY_KEYS.map((_, i) => ({
    dayIndex: i as WeekdayIndex,
    enabled: false,
    timeFrom: '08:00',
    timeTo: '16:00',
  }));

const buildSchedule = (
  entries: WeekDayEntry[],
  startDate: Date,
  endDate: Date,
): ActuatorSetPointDto[] => {
  const result: ActuatorSetPointDto[] = [];

  for (const entry of entries) {
    if (!entry.enabled) continue;

    const jsTargetDay = (entry.dayIndex + 1) % 7;

    const [fromH, fromM] = entry.timeFrom.split(':').map(Number);
    const [toH, toM] = entry.timeTo.split(':').map(Number);

    const firstOccurrence = new Date(startDate);
    firstOccurrence.setHours(0, 0, 0, 0);
    const startDay = firstOccurrence.getDay();
    const daysAhead = (jsTargetDay - startDay + 7) % 7;
    firstOccurrence.setDate(firstOccurrence.getDate() + daysAhead);

    const cursor = new Date(firstOccurrence);
    while (cursor <= endDate) {
      const onTime = new Date(cursor);
      onTime.setHours(fromH ?? 8, fromM ?? 0, 0, 0);

      const offTime = new Date(cursor);
      offTime.setHours(toH ?? 16, toM ?? 0, 0, 0);

      // If posting during an already active interval today, keep the configured
      // start time so the posted interval remains the exact requested window.
      if (onTime < startDate && startDate < offTime && offTime <= endDate) {
        result.push({ timestamp: onTime.toISOString(), value: '0' });
      }

      if (onTime >= startDate && onTime <= endDate) {
        result.push({ timestamp: onTime.toISOString(), value: '0' });
      }
      if (offTime >= startDate && offTime <= endDate) {
        result.push({ timestamp: offTime.toISOString(), value: '100' });
      }

      cursor.setDate(cursor.getDate() + 7);
    }
  }

  result.sort(
    (a, b) =>
      new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime(),
  );

  return result;
};

export const Route = createFileRoute('/$accountId/_user/weekschedule-manager/')(
  {
    component: () => (
      <ServiceGuard
        serviceNames="Scheduler"
        fallback={<Navigate to="/$accountId" />}
      >
        <RouteComponent />
      </ServiceGuard>
    ),
    staticData: { crumb: 'glossary:weekscheduler' },
  },
);

function RouteComponent() {
  const dispatch = useDispatch();
  const { t } = useTranslation('week-schedule-manager');
  const { accountId } = useParams({ strict: false });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedGatewayId, setSelectedGatewayId] = useState<number | ''>('');
  const [channel, setChannel] = useState<string>('output1');
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [weekSchedule, setWeekSchedule] =
    useState<WeekDayEntry[]>(defaultWeekSchedule);

  const { data: gateways, isLoading: isGatewaysLoading } =
    useGetV2GatewaysQuery(
      { accountIds: [Number(accountId)] },
      { skip: !accountId },
    );

  const validGateways = (gateways ?? []).filter(
    (gw) => (gw as { pct?: boolean }).pct === true,
  );

  const { data: activePlans, isLoading: isPlansLoading } =
    useGetAllActuatorPlansQuery(
      {
        accountIds: [Number(accountId)],
        active: true,
      },
      { skip: !accountId },
    );

  const [postActuatorPlan, { isLoading: isCreating }] =
    usePostActuatorPlanMutation();

  const gatewayNamesById = (gateways ?? []).reduce<Record<number, string>>(
    (acc, gw) => {
      if (typeof gw.gatewayId !== 'number') return acc;
      acc[gw.gatewayId] = gw.name ?? `Gateway ${gw.gatewayId}`;
      return acc;
    },
    {},
  );

  const openCreateDialog = () => {
    const hasValidSelectedGateway = validGateways.some(
      (gw) => gw.gatewayId === selectedGatewayId,
    );

    if (
      !hasValidSelectedGateway &&
      typeof validGateways[0]?.gatewayId === 'number'
    ) {
      setSelectedGatewayId(validGateways[0].gatewayId);
    }
    setIsCreateDialogOpen(true);
  };

  const resetCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setChannel('output1');
    setEndDate(defaultEndDate());
    setWeekSchedule(defaultWeekSchedule());
  };

  const toggleWeekEntry = (dayIndex: WeekdayIndex) => {
    setWeekSchedule((prev) => {
      const target = prev.find((entry) => entry.dayIndex === dayIndex);
      if (!target) return prev;

      const nextEnabled = !target.enabled;
      const previousDayIndex = (dayIndex - 1) as WeekdayIndex;
      const previousDay = prev.find(
        (entry) => entry.dayIndex === previousDayIndex,
      );

      const inheritedTimes =
        nextEnabled && previousDay?.enabled
          ? {
              timeFrom: previousDay.timeFrom,
              timeTo: previousDay.timeTo,
            }
          : null;

      return prev.map((entry) => {
        if (entry.dayIndex !== dayIndex) return entry;

        return {
          ...entry,
          enabled: nextEnabled,
          ...(inheritedTimes ?? {}),
        };
      });
    });
  };

  const setWeekEntryRange = (
    dayIndex: WeekdayIndex,
    timeFrom: string,
    timeTo: string,
  ) => {
    setWeekSchedule((prev) =>
      prev.map((entry) =>
        entry.dayIndex === dayIndex
          ? { ...entry, enabled: true, timeFrom, timeTo }
          : entry,
      ),
    );
  };

  const handleCreateSchedule = async () => {
    if (!endDate) {
      dispatch(
        showSnackbar({
          message: t('edit.endDateRequired'),
          severity: 'warning',
        }),
      );
      return;
    }

    const selectedGatewayIsValid = validGateways.some(
      (gw) => gw.gatewayId === selectedGatewayId,
    );

    if (selectedGatewayId === '' || !selectedGatewayIsValid) {
      dispatch(
        showSnackbar({
          message: t('edit.gatewayRequired', {
            defaultValue: 'Select a gateway',
          }),
          severity: 'warning',
        }),
      );
      return;
    }

    const schedule = buildSchedule(weekSchedule, new Date(), new Date(endDate));
    if (schedule.length === 0) {
      dispatch(
        showSnackbar({
          message: t('edit.noScheduleEntries'),
          severity: 'warning',
        }),
      );
      return;
    }

    const result = await postActuatorPlan({
      gatewayId: selectedGatewayId,
      postActuatorPlanDto: {
        channel: channel || null,
        schedule,
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('edit.postSuccess', {
            name:
              gatewayNamesById[selectedGatewayId] ?? String(selectedGatewayId),
          }),
          severity: 'success',
        }),
      );

      resetCreateDialog();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader title={t('title')} disableArrow>
        <Button
          variant="contained"
          size="small"
          onClick={openCreateDialog}
          startIcon={<PostAddRoundedIcon fontSize="small" />}
          disableElevation
        >
          {t('edit.submitButton')}
        </Button>
      </PageHeader>

      <ScheduleDatagrid
        plans={activePlans}
        isLoading={isPlansLoading || isGatewaysLoading}
        gatewayNamesById={gatewayNamesById}
      />

      <InsetDrawer
        open={isCreateDialogOpen}
        onClose={resetCreateDialog}
        title={t('edit.submitButton')}
      >
        <Stack spacing={2}>
          <TextField
            select
            label={t('edit.gatewayLabel')}
            value={selectedGatewayId}
            onChange={(e) => setSelectedGatewayId(Number(e.target.value))}
            size="small"
            fullWidth
          >
            {validGateways.map((gw) => (
              <MenuItem key={gw.gatewayId} value={gw.gatewayId}>
                {gw.name ?? `Gateway ${gw.gatewayId}`}
              </MenuItem>
            ))}
            {validGateways.length === 0 && (
              <MenuItem disabled value="">
                {t('edit.noGatewayOptions', {
                  defaultValue: 'No valid gateways available',
                })}
              </MenuItem>
            )}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              select
              label={t('edit.channelLabel')}
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              size="small"
              fullWidth
            >
              {CHANNEL_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`edit.channelOptions.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label={t('edit.endDateLabel')}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              fullWidth
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </Box>

          <Divider />

          <WeekScheduleEditor
            entries={weekSchedule}
            weekdayKeys={WEEKDAY_KEYS}
            onToggle={(dayIndex) => toggleWeekEntry(dayIndex as WeekdayIndex)}
            onRangeChange={(dayIndex, timeFrom, timeTo) =>
              setWeekEntryRange(dayIndex as WeekdayIndex, timeFrom, timeTo)
            }
          />
        </Stack>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            bottom: 0,
          }}
        >
          <Button onClick={resetCreateDialog}>{t('edit.previewCancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreateSchedule}
            disabled={isCreating}
          >
            {t('edit.submitButton')}
          </Button>
        </Box>
      </InsetDrawer>
    </PageContainer>
  );
}
